import React, { useState, useEffect } from "react";
import { User } from "../../types";
import '../../styles/main.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { configureEspWebInstallButton } from './configureEspWebInstallButton';
import { API_BASE_URL } from '../../config/api';
import ModelSelect from './ModelSelect';

// Update interfaces to match database schema
interface BinFile {
  path: string;
  offset: number;
}

interface Release {
  _id: string;
  model: string;
  version: string;
  type: 'dev' | 'beta' | 'prod' | 'depr' | 'factory';
  hardware?: string;
  forceUpdate: boolean;
  clearState: boolean;
  enabled: boolean;
  bins: BinFile[];
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface Model {
  _id: string;
  name: string;
  code: string;
  description: string;
}

interface UnlimitedFlasherProps {
  user: User;
}

const UnlimitedFlasher: React.FC<UnlimitedFlasherProps> = ({ user }) => {
    const MANIFEST_BASE_URL = API_BASE_URL || 'https://firmware.fluoraplant.com';
  
  const [releases, setReleases] = useState<Release[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>("null");
  const [selectedHardware, setSelectedHardware] = useState<string>("null");
  const [selectedFirmware, setSelectedFirmware] = useState<string>("null");
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [releasesLoading, setReleasesLoading] = useState<boolean>(false);
  const [models, setModels] = useState<Model[]>([]);
  const [availableHardware, setAvailableHardware] = useState<string[]>([]);
  const [showHardwareInfoModal, setShowHardwareInfoModal] = useState<boolean>(false);
  const [selectedBaudRate, setSelectedBaudRate] = useState<number>(2000000);
  
  // Fetch all models (initial load)
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/models`, { headers: { 'Accept': 'application/json' } });
        if (!response.ok) {
          throw new Error("Failed to fetch models");
        }
        const data = await response.json();
        setModels(data.models || []);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setInitialLoading(false);
      }
    };
    fetchModels();
  }, [API_BASE_URL]);

  // Fetch hardware revisions when a model is selected
  useEffect(() => {
    if (selectedProduct && selectedProduct !== "null") {
      const fetchHardware = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/hardware/type/${encodeURIComponent(selectedProduct)}`, { headers: { 'Accept': 'application/json' } });
          if (!response.ok) {
            throw new Error("Failed to fetch hardware revisions");
          }
          const data = await response.json();
          // Extract revision numbers from hardware objects
          const revisions = (data.hardware || []).map((hw: any) => hw.revision.toString()).sort();
          setAvailableHardware(revisions);
        } catch (error: any) {
          setError(error.message);
          setAvailableHardware([]);
        }
      };
      fetchHardware();
    } else {
      setAvailableHardware([]);
      setSelectedHardware("null");
    }
  }, [selectedProduct, API_BASE_URL]);

  // Fetch releases when a model and hardware are selected
  useEffect(() => {
    if (selectedProduct && selectedProduct !== "null") {
      const fetchReleases = async () => {
        setReleasesLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/releases/${encodeURIComponent(selectedProduct)}`, { headers: { 'Accept': 'application/json' } });
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
            console.error("Failed to fetch releases:", response.status, errorMessage, "for model:", selectedProduct);
            throw new Error(`Failed to fetch releases: ${errorMessage}`);
          }
          const data = await response.json();
          // Filter for enabled releases only (all types for unlimited)
          const enabledReleases = (data.releases || []).filter((release: any) => release.enabled);
          setReleases(enabledReleases);
        } catch (error: any) {
          setError(error.message);
          setReleases([]);
        } finally {
          setReleasesLoading(false);
        }
      };
      fetchReleases();
    } else {
      setReleases([]);
    }
  }, [selectedProduct, API_BASE_URL]);

  // Handle product selection change
  const handleProductChange = (productCode: string) => {
    // Find the model by code to get its name
    const selectedModel = models.find(m => m.code === productCode);
    if (selectedModel) {
      setSelectedProduct(selectedModel.name); // Use the model name for releases
      setSelectedHardware("null"); // Reset hardware selection
      setSelectedFirmware("null"); // Reset firmware selection
      setSelectedRelease(null); // Reset selected release
    }
  };

  // Baud rate options
  const baudRateOptions = [
    { value: 38400, label: "38400" },
    { value: 57600, label: "57600" },
    { value: 115200, label: "115200" },
    { value: 230400, label: "230400" },
    { value: 460800, label: "460800" },
    { value: 921600, label: "921600" },
    { value: 2000000, label: "2000000 (2 Mbps) (Default)" }
  ];

  // Handle hardware selection change
  const handleHardwareChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedHardware(e.target.value);
    setSelectedFirmware("null"); // Reset firmware selection
    setSelectedRelease(null); // Reset selected release
  };

  // Handle baud rate selection change
  const handleBaudRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBaudRate(parseInt(e.target.value, 10));
  };

  // Handle firmware version selection change
  const handleFirmwareChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const firmwareVersion = e.target.value;
    setSelectedFirmware(firmwareVersion);
    
    if (selectedProduct !== "null" && selectedHardware !== "null" && firmwareVersion !== "null") {
      // Find the selected firmware in the releases
      const firmwareInfo = releases.find(
        fw => fw.model === selectedProduct && 
              fw.version === firmwareVersion && 
              fw.enabled
      );
      
      if (firmwareInfo) {
        setSelectedRelease(firmwareInfo);
        console.log(`${MANIFEST_BASE_URL}/config/manifest/${firmwareInfo.model.replace(/\s+/g, '_')}-rev${selectedHardware}-v${firmwareInfo.version}.json`)
      } else {
        setSelectedRelease(null);
      }
    } else {
      setSelectedRelease(null);
    }
  };

  // Get available firmware versions for selected product - ALL types
  const getAvailableFirmwareVersions = () => {
    if (selectedProduct === "null") return [];
    
    // Get firmware versions from releases for the selected product
    const availableReleases = releases
      .filter(release => 
        release.model === selectedProduct && 
        release.enabled
      )
      .sort((a, b) => {
        // Compare version strings in reverse order (for newest first)
        return b.version.localeCompare(a.version, undefined, { numeric: true, sensitivity: 'base' });
      });
    
    return availableReleases;
  };

  // Ref for the install button container
  const installButtonRef = React.useRef<HTMLDivElement>(null);

  // Create/update the install button element
  React.useEffect(() => {
    if (!installButtonRef.current) return;
    
    // Clear existing content
    installButtonRef.current.innerHTML = '';
    
    if (!selectedRelease) {
      return;
    }

    // Create the custom element
    const installButton = document.createElement('esp-web-install-button');
    configureEspWebInstallButton(installButton, {
      manifest: `${MANIFEST_BASE_URL}/config/manifest/${selectedProduct.replace(/\s+/g, '_')}-rev${selectedHardware}-v${selectedRelease.version}.json`,
      baudRate: selectedBaudRate,
      trackFirmware: {
        model: selectedProduct,
        toFirmware: selectedRelease.version,
      },
    });
    
    // Create the button element
    const button = document.createElement('button');
    button.setAttribute('slot', 'activate');
    button.className = 'btn btn-primary btn-lg';
    button.textContent = 'Connect To Device';
    
    installButton.appendChild(button);
    installButtonRef.current.appendChild(installButton);
  }, [selectedRelease, selectedProduct, selectedHardware, selectedBaudRate, API_BASE_URL]);

  // Install button container
  const InstallButton = <div ref={installButtonRef} />;

  if (error) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  if (initialLoading) {
    return (
      <div className="container-fluid mt-3">
        <div className="row mb-3">
          <div className="col">
            <h3>Firmware Flasher</h3>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6 mx-auto text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading firmware data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="row mb-3">
        <div className="col">
          <h3>Firmware Flasher</h3>
        </div>
      </div>
      
      <div className="row g-4 align-items-start">
        <div className="col-12 col-lg-4">
          {/* Flasher Controls */}
          <div>
            {/* Step 1: Product Selection */}
            <div className="mb-4">
              <label htmlFor="product_model" className="form-label fw-bold">Model</label>
              <ModelSelect
                id="product_model"
                className="mb-3"
                models={models}
                value={selectedProduct === "null" ? "" : models.find(m => m.name === selectedProduct)?.code || ""}
                onChange={handleProductChange}
              />
            </div>

            {/* Step 2: Hardware Revision Selection */}
            <div className="mb-4">
              <label htmlFor="hardware_revision" className="form-label fw-bold me-0">
                Hardware Revision
                <button
                  type="button"
                  className="btn btn-link p-0 ms-1 align-middle"
                  onClick={() => setShowHardwareInfoModal(true)}
                  style={{ color: "inherit", textDecoration: "none", position: "relative", top: "-1px" }}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-secondary"
                    size="lg"
                    aria-label="More information about hardware versions"
                  />
                </button>
              </label>
              <select 
                id="hardware_revision" 
                className="form-select mb-3" 
                onChange={handleHardwareChange}
                value={selectedHardware === "null" ? "" : selectedHardware}
                disabled={selectedProduct === "null"}
                title="Select the hardware revision of your device. You can find this in the Device Settings screen of the mobile app (press the wrench icon)."
              >
                <option value="null">Choose Hardware Revision...</option>
                {availableHardware.map((hardware, index) => (
                  <option key={index} value={hardware}>
                    Rev {hardware}
                  </option>
                ))}
              </select>
              {/* Hardware Info Modal */}
              {showHardwareInfoModal && (
                <>
                  <div
                    className="modal-backdrop show"
                    onClick={() => setShowHardwareInfoModal(false)}
                  ></div>
                  <div
                    className="modal d-block"
                    tabIndex={-1}
                    role="dialog"
                    aria-labelledby="hardwareInfoModalLabel"
                    aria-hidden="true"
                  >
                    <div className="modal-dialog" role="document">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title" id="hardwareInfoModalLabel">Hardware Revision</h5>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={() => setShowHardwareInfoModal(false)}
                          ></button>
                        </div>
                        <div className="modal-body">
                        <h5>How to Determine Your Hardware Revision</h5>
                          <p>The hardware revision can be found on the PCB located inside of the product, printed under the logo.</p>
                          <img
                            src="/hardware-revision-location.png"
                            alt="Location of the hardware revision on the Fluora and Monos PCBs"
                            className="img-fluid rounded border mb-3"
                          />
                          <p className="alert alert-info">
                            <strong>Note:</strong> If you received a Fluora Mini or Fluora Floor before December 2025, it is likely to be a revision 4. If after, it is likely to be a revision 5.
                          </p>
                          <p className="alert alert-warning">
                            <strong>Important:</strong> Flashing firmware for an incorrect hardware revision may cause your device to malfunction.
                            If you're unsure, please contact customer support.
                          </p>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowHardwareInfoModal(false)}
                          >
                            Got it
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Step 3: Firmware Version Selection */}
            <div className="mb-4">
              <label htmlFor="firmware_version" className="form-label fw-bold">Firmware Version</label>
              <select 
                id="firmware_version" 
                className="form-select mb-3" 
                onChange={handleFirmwareChange}
                value={selectedFirmware === "null" ? "" : selectedFirmware}
                disabled={selectedProduct === "null" || releasesLoading}
              >
                <option value="null">Choose A Firmware Version...</option>
                {releasesLoading ? (
                  <option>Loading...</option>
                ) : (
                  getAvailableFirmwareVersions().map((release, index) => (
                    <option key={index} value={release.version}>
                      {release.version} ({release.type})
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Step 4: Baud Rate Selection */}
            <div className="mb-4">
              <label htmlFor="baud_rate" className="form-label fw-bold">Baud Rate</label>
              <select 
                id="baud_rate" 
                className="form-select mb-3" 
                onChange={handleBaudRateChange}
                value={selectedBaudRate}
              >
                {baudRateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Step 5: Connect to Device Button */}
            <div className="mb-4">
              {InstallButton}
            </div>
          </div>
        </div>

        {/* Quick Instructions Section */}
        <div className="col-12 col-lg-4">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Quick Instructions</h5>
            </div>
            <div className="card-body">
              <ol className="mb-0" style={{ fontSize: '0.9em' }}>
                <li>Access USB connector</li>
                <li>Plug USB in to computer and device</li>
                <li>Confirm device is powered on and red indicator LED light is illuminated</li>
                <li>Select Model and Firmware</li>
                <li>Click "Connect To Device"</li>
                <li>Select "USB"</li>
                <li>Follow instructions on screen</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnlimitedFlasher;